// src/components/padrekko/qc/resolutionCopy.js
//
// What the analyst is told about the geometry their pad was analyzed on.
//
// A separate module rather than JSX, so it can be tested. The three fallback
// notes mean three different things and carry three different remedies, and a
// banner that blurs them is worse than no banner: `feature_clipped` is an
// accusation about the analyst's AOI with something they can do about it, and
// `no_feature` is not an accusation at all.
//
// ── THE CLIPPED BAND COVERS TWO GEOMETRIES ──────────────────────────────────
//
// The AOI cuts a pad's edge (96.33% of a detected pad inside, which is
// UNIVERSITY 6-12 3 on dev), and the AOI sits inside a much larger pad (a
// 271,370 m2 pad can never be more than ~15% inside a 200 m box). Same remedy —
// extend the AOI — so one band is correct. But a sentence written for one reads
// as nonsense at the other: "your AOI is cutting the edge off a pad" is wrong
// at 15%, and "this pad is much larger than your AOI" is wrong at 96%.
//
// SO THE SENTENCE STATES THE FRACTION AND LETS IT SPEAK. "96% of a detected pad
// is inside this AOI" reads as an edge being clipped. "15% of a detected pad is
// inside this AOI" reads as an AOI sitting inside something bigger. One
// sentence, no branch on magnitude, no editorializing about which case it is.

/** Metres per pixel of a WebMercatorQuad tile at `zoom`, in EPSG:3857. */
const PROJECTED_M_PER_PX = (zoom) => 156543.03392804097 / Math.pow(2, zoom);

/**
 * GROUND centimetres per pixel — projected, times cos(latitude).
 *
 * NAMED "ground" EVERYWHERE IT APPEARS. aiprime.datasets.resolution_cm is
 * documented as ground resolution and actually holds the EPSG:3857 projected
 * figure (REM-398), and the two differ by ~15% at these latitudes. Any number
 * this app shows as cm/px has to say which one it is, or it inherits that
 * confusion. Elsewhere the platform shows the projected figure unlabelled, in
 * ImageryCoveragePanel and DatasetCoveragePanel — neither of which can be on
 * screen with the QC inspector, since they need activeProduct === 'explore'
 * and this needs 'padrekko'.
 *
 * Exact only when the crop was not downscaled. On the resolved arm
 * chooseZoomForFrame guarantees that by construction; on the fallback arm it is
 * not guaranteed, which is why this is shown only for a resolved pad.
 *
 * @param {number} zoom
 * @param {number} lat  latitude of the frame, degrees
 * @returns {number|null}
 */
export function groundCmPerPixel(zoom, lat) {
  if (!Number.isFinite(zoom) || !Number.isFinite(lat)) return null;
  return PROJECTED_M_PER_PX(zoom) * Math.cos((lat * Math.PI) / 180) * 100;
}

/** A percentage with no decimals, floored, so 0.9633 reads 96 and never 97. */
function pct(frac) {
  if (!Number.isFinite(frac)) return null;
  return Math.floor(frac * 100);
}

/**
 * The banner for a pad's resolution.
 *
 * @param {object|null} pad  a pad row as GET /cases/:case_id returns it, plus
 *   `containment_frac` when a fresh resolve supplied one.
 * @returns {{tone:'resolved'|'attention'|'neutral', title:string,
 *            detail:string|null, action:string|null}|null}
 *   null when the pad has never been resolved — there is nothing to say yet,
 *   and a banner saying "unknown" is noise.
 */
export function resolutionCopy(pad) {
  if (!pad || !pad.geometry_source) return null;

  if (pad.geometry_source === "pad_feature") {
    const lat = Array.isArray(pad.bbox) && pad.bbox.length === 4
      ? (pad.bbox[1] + pad.bbox[3]) / 2
      : null;
    const cm = lat === null ? null : groundCmPerPixel(pad.resolved_zoom, lat);
    const bits = [];
    if (cm !== null) bits.push(`${cm.toFixed(1)} cm/px on the ground`);
    if (pad.source_dataset_key) bits.push(`imagery ${datasetDate(pad.source_dataset_key)}`);
    return {
      tone: "resolved",
      title: "Analyzed on the detected pad outline",
      detail: bits.length ? bits.join(" · ") : null,
      action: null,
    };
  }

  // Every other arm analyzed the AOI as drawn. What differs is why.
  switch (pad.resolution_note) {
    case "feature_clipped": {
      const p = pct(pad.containment_frac);
      return {
        tone: "attention",
        title: p === null
          // WITHOUT THE FRACTION. Migration 123 does not store it: the arm CHECK
          // requires source_feature_id IS NULL on user_aoi, so the clipped
          // feature's identity is not kept and the number cannot be recovered
          // after a reload. True at both ends of the band, and says less.
          ? "This AOI and a detected pad only partly overlap"
          : `${p}% of a detected pad is inside this AOI`,
        detail: "The analysis ran on the AOI as drawn.",
        action: "Extend the AOI to cover the whole pad, then resolve again.",
      };
    }
    case "ambiguous_match":
      return {
        tone: "attention",
        title: "Two or more detected pads sit inside this AOI",
        detail: "The analysis ran on the AOI as drawn. Neither pad was chosen, "
              + "and they were not combined.",
        action: "Split this into one pad per AOI to analyze each separately.",
      };
    case "no_feature":
      return {
        tone: "neutral",
        title: "No detected pad in this AOI",
        // NOT AN ACCUSATION, AND NO ACTION. Nothing is wrong and there is
        // nothing for the analyst to fix; saying otherwise sends them to look
        // for a mistake they did not make.
        detail: "The analysis ran on the AOI as drawn.",
        action: null,
      };
    default:
      return {
        tone: "neutral",
        title: "Analyzed on the AOI as drawn",
        detail: null,
        action: null,
      };
  }
}

/** `imagery.permian.midland.2025_12_13` -> `2025-12-13`; anything else, as-is. */
export function datasetDate(key) {
  const m = String(key || "").match(/(\d{4})_(\d{2})_(\d{2})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : String(key || "");
}
